import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, History, RefreshCw, ShieldCheck } from "lucide-react";
import { API } from "../../auth/AuthContext";
import AdminPagination from "../../components/admin/AdminPagination";
import { isAiManualReviewStatus } from "../../lib/inspectionAiStatus";
import { buildFraudHistoryQuery, fraudSubject, FRAUD_HISTORY_PAGE_SIZE } from "./fraudAdmin";

const EMPTY_FILTERS = {
  status: "all", date_from: "", date_to: "", vehicle_id: "", booking_id: "",
};

function FraudDetail({ detail }) {
  return (
    <div className="mt-4 p-4 rounded-xl bg-[var(--zc-bg-3)] border border-[var(--zc-border)]" data-testid="fraud-detail">
      <div className="grid sm:grid-cols-2 gap-3 text-xs text-[var(--zc-text-2)]">
        <div><span className="text-[var(--zc-text-3)]">Submitted by:</span> {detail.submitted_by || "Deleted user"}</div>
        <div><span className="text-[var(--zc-text-3)]">Role:</span> {detail.submitter_role || "—"}</div>
        <div><span className="text-[var(--zc-text-3)]">Odometer:</span> {detail.odometer_km ?? detail.odometer_mi ?? "—"}</div>
        <div><span className="text-[var(--zc-text-3)]">Fuel:</span> {detail.fuel_pct != null ? `${detail.fuel_pct}%` : "—"}</div>
        {detail.ai_assessment?.status && (
          <div>
            <span className="text-[var(--zc-text-3)]">AI status:</span>{" "}
            <span className="font-mono">{detail.ai_assessment.status}</span>
          </div>
        )}
      </div>
      {detail.notes && <p className="text-sm text-[var(--zc-text-2)] mt-3">{detail.notes}</p>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
        {(detail.photos || []).map((photo) => (
          <div key={`${photo.angle}-${photo.file_id}`} className="min-w-0">
            {photo.available ? (
              <img src={photo.url} alt={`${photo.angle || "Inspection"} evidence`} className="w-full aspect-video object-cover rounded-xl" />
            ) : (
              <div className="w-full aspect-video rounded-xl bg-[var(--zc-bg-2)] border border-[var(--zc-border)] flex items-center justify-center text-xs text-[var(--zc-text-3)] text-center px-2">
                Media deleted or unavailable
              </div>
            )}
            <div className="text-xs text-[var(--zc-text-3)] mt-1 capitalize">{(photo.angle || "photo").replace(/_/g, " ")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FraudRows({
  rows,
  empty,
  selectedId,
  detail,
  detailLoading,
  onDetail,
  retryingId,
  onRetryVision,
}) {
  if (rows.length === 0) {
    return <div className="px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">{empty}</div>;
  }
  return rows.map((row) => {
    const hostLabel = row.host_available === false ? "Deleted user" : row.host_name || row.host_email || "—";
    const aiStatus = row.ai_status
      || (selectedId === row.inspection_id ? detail?.ai_assessment?.status : null);
    const canRetry = isAiManualReviewStatus(aiStatus);
    return (
      <div key={row.inspection_id} className="px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] last:border-0">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="zc-title text-base capitalize">{row.phase?.replace(/_/g, " ") || "Inspection"} · {fraudSubject(row)}</div>
            <div className="text-xs text-[var(--zc-text-3)] mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
              <span>Vehicle <span className="font-mono text-[var(--zc-text-2)]">{row.vehicle_id || "—"}</span></span>
              <span>·</span>
              <span>Host <span className="text-[var(--zc-text-2)]">{hostLabel}</span></span>
            </div>
            <div className="text-xs text-[var(--zc-text-3)] mt-1">
              Booking {row.booking_id || "—"} · {row.created_at ? new Date(row.created_at).toLocaleString() : ""}
            </div>
          </div>
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
            row.resolution_status === "resolved"
              ? "bg-[var(--zc-secondary-soft)] text-[var(--zc-secondary)]"
              : "bg-[var(--zc-accent-soft)] text-[var(--zc-danger)]"
          }`}>
            {row.resolution_status || "open"}
          </span>
        </div>
        {row.fraud_flags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {row.fraud_flags.map((flag) => (
              <span key={flag} className="text-xs uppercase tracking-wide px-2 py-0.5 rounded bg-[var(--zc-bg-3)] text-[var(--zc-text-3)]">{flag.replace(/_/g, " ")}</span>
            ))}
          </div>
        )}
        {row.legacy_evidence_erased && (
          <p className="text-xs text-[var(--zc-text-3)] mt-2">Legacy resolved record — original flags were cleared before evidence retention was introduced.</p>
        )}
        {row.session_consistency_score != null && <div className="text-xs text-[var(--zc-text-3)] mt-2">Session consistency: {row.session_consistency_score}/100</div>}
        {aiStatus && isAiManualReviewStatus(aiStatus) && (
          <p className="text-xs text-[var(--zc-warn)] mt-2 font-semibold">
            AI status: {aiStatus.replace(/_/g, " ")}
          </p>
        )}
        {row.ai_summary && <p className="text-sm text-[var(--zc-text-2)] mt-2 line-clamp-3">{row.ai_summary}</p>}
        {row.resolved_at && (
          <div className="text-xs text-[var(--zc-text-3)] mt-2">
            Resolved {new Date(row.resolved_at).toLocaleString()}{row.resolution_reason ? ` · ${row.resolution_reason.replace(/_/g, " ")}` : ""}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          <button type="button" className="zc-btn-ghost h-8 px-3 text-xs" onClick={() => onDetail(row.inspection_id)}>
            {selectedId === row.inspection_id ? "Hide details" : "View evidence"}
          </button>
          {canRetry && (
            <button
              type="button"
              className="zc-btn-outline-primary h-8 px-3 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              disabled={retryingId === row.inspection_id}
              onClick={() => onRetryVision(row.inspection_id)}
              data-testid={`retry-vision-${row.inspection_id}`}
            >
              <RefreshCw size={12} className={retryingId === row.inspection_id ? "animate-spin" : ""} />
              {retryingId === row.inspection_id ? "Retrying…" : "Retry AI vision"}
            </button>
          )}
        </div>
        {selectedId === row.inspection_id && (detailLoading ? (
          <p className="text-xs text-[var(--zc-text-3)] mt-3">Loading evidence…</p>
        ) : detail ? <FraudDetail detail={detail} /> : null)}
      </div>
    );
  });
}

export default function AdminFraudTab({
  fraudQueue,
  page,
  totalPages,
  onPageChange,
}) {
  const [view, setView] = useState("open");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [aiStatusById, setAiStatusById] = useState({});

  const loadHistory = async ({ page: nextPage = 1, nextFilters = filters } = {}) => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/inspections/fraud-history${buildFraudHistoryQuery(nextFilters, null)}&page=${nextPage}`,
        { withCredentials: true },
      );
      setHistoryRows(response.data.items);
      setHistoryTotal(response.data.total ?? null);
      setHistoryPage(nextPage);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = () => {
    setView("history");
    if (historyRows.length === 0) loadHistory();
  };

  const toggleDetail = async (inspectionId) => {
    if (selectedId === inspectionId) {
      setSelectedId(null);
      setDetail(null);
      return;
    }
    setSelectedId(inspectionId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/admin/inspections/fraud-history/${inspectionId}`, { withCredentials: true });
      setDetail(response.data);
      const status = response.data?.ai_assessment?.status || response.data?.ai_status;
      if (status) {
        setAiStatusById((prev) => ({ ...prev, [inspectionId]: status }));
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const retryVision = async (inspectionId) => {
    setRetryingId(inspectionId);
    try {
      const response = await axios.post(
        `${API}/admin/inspections/${inspectionId}/retry-vision`,
        {},
        { withCredentials: true },
      );
      const status = response.data?.status;
      setAiStatusById((prev) => ({ ...prev, [inspectionId]: status }));
      if (selectedId === inspectionId && detail) {
        setDetail({
          ...detail,
          ai_assessment: response.data?.ai_assessment || detail.ai_assessment,
          ai_status: status,
        });
      }
      if (isAiManualReviewStatus(status)) {
        toast.error(`Vision still ${String(status || "").replace(/_/g, " ")}`);
      } else {
        toast.success(`Vision retry complete · ${status || "updated"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vision retry failed");
    } finally {
      setRetryingId(null);
    }
  };

  const withAiStatus = (rows) =>
    (rows || []).map((row) => ({
      ...row,
      ai_status: aiStatusById[row.inspection_id] || row.ai_status,
    }));

  return (
    <div className="zc-card overflow-hidden mt-4" data-testid="admin-fraud-queue">
      <div className="px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} className="text-[var(--zc-warn)]" />
          <span className="text-sm font-semibold">Inspection fraud review</span>
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={() => setView("open")} className={`h-9 px-3 text-xs ${view === "open" ? "zc-btn-outline-primary" : "zc-btn-ghost"}`}>
            <ShieldCheck size={13} className="inline mr-1" /> Open
          </button>
          <button type="button" onClick={openHistory} className={`h-9 px-3 text-xs ${view === "history" ? "zc-btn-outline-primary" : "zc-btn-ghost"}`}>
            <History size={13} className="inline mr-1" /> History
          </button>
        </div>
      </div>

      {view === "history" && (
        <form className="p-4 sm:p-6 bg-[var(--zc-bg-3)] border-b border-[var(--zc-border)]" onSubmit={(event) => { event.preventDefault(); loadHistory(); }}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <label className="text-xs text-[var(--zc-text-2)]">Status
              <select className="zc-input w-full mt-1" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="all">All</option><option value="open">Open</option><option value="resolved">Resolved</option>
              </select>
            </label>
            <label className="text-xs text-[var(--zc-text-2)]">From
              <input type="date" className="zc-input w-full mt-1" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            </label>
            <label className="text-xs text-[var(--zc-text-2)]">To
              <input type="date" className="zc-input w-full mt-1" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </label>
            <label className="text-xs text-[var(--zc-text-2)]">Vehicle ID
              <input className="zc-input w-full mt-1" value={filters.vehicle_id} onChange={(e) => setFilters({ ...filters, vehicle_id: e.target.value })} />
            </label>
            <label className="text-xs text-[var(--zc-text-2)]">Booking ID
              <input className="zc-input w-full mt-1" value={filters.booking_id} onChange={(e) => setFilters({ ...filters, booking_id: e.target.value })} />
            </label>
          </div>
          <div className="flex items-center justify-between gap-3 mt-4">
            <button type="submit" className="zc-btn-primary h-9 px-4 text-xs" disabled={historyLoading}>Apply filters</button>
            {historyTotal != null && <span className="text-xs text-[var(--zc-text-3)]">{historyTotal} assessment{historyTotal === 1 ? "" : "s"}</span>}
          </div>
        </form>
      )}

      <FraudRows
        rows={withAiStatus(view === "open" ? fraudQueue : historyRows)}
        empty={view === "open" ? "No flagged inspections — queue is clear." : "No assessments match these filters."}
        selectedId={selectedId}
        detail={detail}
        detailLoading={detailLoading}
        onDetail={toggleDetail}
        retryingId={retryingId}
        onRetryVision={retryVision}
      />
      {view === "open" ? (
        <AdminPagination page={page} totalPages={totalPages} onPageChange={onPageChange} testid="fraud-pagination" />
      ) : (
        <AdminPagination
          page={historyPage}
          totalPages={Math.max(1, Math.ceil((historyTotal || 0) / FRAUD_HISTORY_PAGE_SIZE))}
          onPageChange={(p) => loadHistory({ page: p })}
          testid="fraud-history-pagination"
        />
      )}
    </div>
  );
}
